import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/cn";
import { buttonVariants } from "./buttonVariants";
import { Spinner } from "../v2/spinner";

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      isLoading?: boolean;
    }
>(
  (
    { className, variant, size, asChild = false, isLoading = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // Slot requires exactly one child to merge onto — the spinner + label
    // wrapper below would hand it two. asChild consumers (links styled as
    // buttons) don't load, so pass children straight through.
    if (asChild) {
      return (
        <Comp
          data-slot="button"
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        />
      );
    }

    const spinnerSizeMap: Record<string, "sm" | "default" | "lg" | "xl"> = {
      lg: "lg",
      "icon-lg": "lg",
      sm: "sm",
      xs: "sm",
      icon: "sm",
      "icon-sm": "sm",
      "icon-xs": "sm",
      default: "default",
    };
    const spinnerSize = spinnerSizeMap[size as string] || "default";

    return (
      <Comp
        data-slot="button"
        disabled={isLoading || props.disabled}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {isLoading && (
          <Spinner
            size={spinnerSize}
            wrapperClassName="absolute"
            className="text-current"
          />
        )}
        <span
          className={cn(
            "inline-flex items-center justify-center gap-2",
            isLoading ? "opacity-0 ms-2" : "opacity-100",
          )}
        >
          {props.children}
        </span>
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button };
